import { FC } from 'react';
import { FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  ButtonPrimary,
  Form,
  InputSelect,
  InputSwitch,
  InputText,
  ModalActions,
  useSnackbar
} from '@lambdacurry/component-library';
import { StripePaymentMethod } from 'lib/home/generated/types/api';
import { addressValidation } from 'lib/forms/validation';
import { monthOptions, getYearOptions, currentYear } from '../../../util/helpers/forms';
import { AddressInputGroup } from '../../InputGroups';
import { useUpdateMutation } from '../PaymentMethod.hooks';

const validationSchema = Yup.object({
  card: Yup.object({
    exp_month: Yup.string().required('Expiry Month is required'),
    exp_year: Yup.string().required('Expiry Year is required')
  }),
  billing_details: Yup.object({
    name: Yup.string().required('Full Name is required'),
    phone: Yup.string().nullable(),
    email: Yup.string().nullable().email('Please enter a valid email address.'),
    address: Yup.object(addressValidation)
  })
});

export interface EditPaymentMethodFormProps {
  paymentMethod: {
    id: StripePaymentMethod['id'];
    card: Pick<StripePaymentMethod['card'], 'exp_month' | 'exp_year'>;
    billing_details: StripePaymentMethod['billing_details'];
    is_default?: StripePaymentMethod['is_default'];
  };
  onSave: (values: any, formikHelpers: FormikHelpers<any>) => void;
  onCancel: () => void;
}

const yearOptions = getYearOptions(currentYear, currentYear + 20);

export const EditPaymentMethodForm: FC<EditPaymentMethodFormProps> = ({ paymentMethod, onSave, onCancel }) => {
  const updateMutation = useUpdateMutation();
  const { addSnackbar } = useSnackbar();

  const { id, card, billing_details } = paymentMethod;

  const initialValues: EditPaymentMethodFormProps['paymentMethod'] = {
    id,
    card: {
      exp_month: card.exp_month,
      exp_year: card.exp_year
    },
    billing_details: {
      ...billing_details
    },
    is_default: !!paymentMethod.is_default
  };

  const handleSubmit = (values: typeof initialValues, formikActions: FormikHelpers<typeof initialValues>) => {
    formikActions.setSubmitting(true);

    updateMutation.mutate(
      { updatePaymentMethodInput: values },
      {
        onSuccess: () => {
          addSnackbar('Payment method updated!', { variant: 'success' });
          if (onSave) onSave(values, formikActions);
        },
        onError: () => formikActions.setSubmitting(false)
      }
    );
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  return (
    <Form initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
      {formikProps => (
        <>
          <InputText name="billing_details.name" label="Full Name" />

          <AddressInputGroup name="billing_details.address" />

          <div className="flex gap-4">
            <div className="flex-grow md:w-1/2 md:basis-1/2">
              <InputSelect name="card.exp_month" label="Expiry Month" options={monthOptions} optionValueKey="value" />
            </div>
            <div className="flex-grow md:w-1/2 md:basis-1/2">
              <InputSelect name="card.exp_year" label="Expiry Year" options={yearOptions} optionValueKey="value" />
            </div>
          </div>

          {!paymentMethod.is_default && (
            <InputSwitch name="is_default" label="Set as default" data-cy="set-default-payment-method-toggle" />
          )}

          <ModalActions className="!mt-4">
            <ButtonPrimary
              className="!flex-grow-0"
              type="submit"
              disabled={!formikProps.dirty || formikProps.isSubmitting}
            >
              {formikProps.isSubmitting ? 'Updating...' : 'Update'}
            </ButtonPrimary>
            {formikProps.dirty && (
              <Button className="!flex-grow-0" onClick={handleCancel} disabled={formikProps.isSubmitting}>
                Cancel
              </Button>
            )}
          </ModalActions>
        </>
      )}
    </Form>
  );
};
